import { MutableRefObject, useLayoutEffect, useRef, useState } from "react";

interface IUseElementWidthLayoutReturn {
  ref: MutableRefObject<HTMLDivElement | null>;
  width: number;
}

// Layout-effect variant of width measurement. Reads `clientWidth` inside
// `useLayoutEffect` so the first paint already has the measured width — needed
// when downstream layout (e.g. a cropper container) depends on the width to
// avoid a one-frame flash.
export const useElementWidthLayout = (): IUseElementWidthLayoutReturn => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);

  // Empty deps + `ref.current` snapshot: assumes `ref` is attached to an
  // unconditionally-mounted element. Conditional refs (e.g. {visible && <div
  // ref={ref}/>}) need a different pattern (callback ref or dep on the flag).
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = (): void => setWidth(el.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, width };
};
